// src/components/HR/HrReportsDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  Fade,
  Skeleton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  EventNote as EventNoteIcon,
  EmojiEvents as AwardIcon,
  Work as WorkIcon,
  DateRange as DateRangeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Business as BusinessIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  PeopleAlt as PeopleAltIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Cake as CakeIcon,
  Celebration as CelebrationIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

import Sidebar from '../components/sidebars/Sidebar';
import TopNav from '../components/top_bars/TopNav';

// Color palettes
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];
const STATUS_COLORS = {
  pending: '#ff9800',
  approved: '#4caf50',
  rejected: '#f44336',
  completed: '#2196f3',
  active: '#4caf50',
  inactive: '#9e9e9e',
};

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan-Mar)' },
  { value: 2, label: 'Q2 (Apr-Jun)' },
  { value: 3, label: 'Q3 (Jul-Sep)' },
  { value: 4, label: 'Q4 (Oct-Dec)' },
];

const HrReportsDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // State
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [salaryData, setSalaryData] = useState(null);
  const [leaveData, setLeaveData] = useState(null);
  const [turnoverData, setTurnoverData] = useState(null);
  const [deptData, setDeptData] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [demographicsData, setDemographicsData] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState(null);
  const [apiErrors, setApiErrors] = useState({});
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userRole, setUserRole] = useState('gm');

  // Helper functions
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₦0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return '₦0.00';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(num);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('[HR Reports] Date parsing error:', error);
      return 'N/A';
    }
  };

  const getApiBaseUrl = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    return baseUrl.replace(/\/$/, '');
  };

  // Debug helper to log data for troubleshooting
  const debugLogData = useCallback((dataType, data) => {
    if (dataType === 'dashboard' && data?.summary) {
      console.log(`[HR Reports Debug] Birthdays this month: ${data.summary.birthdays_this_month}`);
      console.log(`[HR Reports Debug] Pending leave: ${data.summary.pending_leave}`);
      console.log(`[HR Reports Debug] Employees on leave: ${data.summary.employees_on_leave}`);
    }
    if (dataType === 'leave') {
      console.log(`[HR Reports Debug] Leave requests by status:`, data?.requests_by_status);
      console.log(`[HR Reports Debug] Pending requests count:`, data?.pending_requests?.length);
      console.log(`[HR Reports Debug] Leave balance summary:`, data?.balance_summary);
    }
    if (dataType === 'demographics') {
      console.log(`[HR Reports Debug] Age distribution:`, data?.age_distribution);
      console.log(`[HR Reports Debug] Marital status:`, data?.marital_status);
    }
    if (dataType === 'events') {
      console.log(`[HR Reports Debug] Upcoming birthdays:`, data?.upcoming_birthdays);
      console.log(`[HR Reports Debug] Leaves ending soon:`, data?.leaves_ending_soon);
    }
  }, []);

  // Fetch all data with error handling for each endpoint
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    const errors = {};

    try {
      const baseUrl = getApiBaseUrl();
      
      // Helper function to fetch with error handling
      const fetchWithErrorHandling = async (url, actionName) => {
        try {
          console.log(`[HR Reports] Fetching ${actionName} from: ${url}`);
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const result = await response.json();
          if (result.success) {
            // Debug log the data
            debugLogData(actionName, result.data);
            return result.data;
          } else {
            errors[actionName] = result.message || `Failed to load ${actionName}`;
            console.error(`[HR Reports] API error for ${actionName}:`, result.message);
            return null;
          }
        } catch (error) {
          console.error(`[HR Reports] Error fetching ${actionName}:`, error);
          errors[actionName] = error.message;
          return null;
        }
      };

      // Fetch all data in parallel
      const [
        dashboardDataResult,
        employeeDataResult,
        attendanceDataResult,
        salaryDataResult,
        leaveDataResult,
        turnoverDataResult,
        deptDataResult,
        payrollDataResult,
        performanceDataResult,
        demographicsDataResult,
        eventsDataResult,
      ] = await Promise.all([
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=dashboard&year=${selectedYear}&month=${selectedMonth}`, 'dashboard'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=employee_summary`, 'employee'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=attendance_report&year=${selectedYear}&month=${selectedMonth}`, 'attendance'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=salary_report&year=${selectedYear}&month=${selectedMonth}`, 'salary'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=leave_report&year=${selectedYear}`, 'leave'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=turnover_report&year=${selectedYear}`, 'turnover'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=department_stats`, 'department'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=payroll_summary&year=${selectedYear}`, 'payroll'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=performance_report&year=${selectedYear}&quarter=${selectedQuarter}`, 'performance'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=demographics`, 'demographics'),
        fetchWithErrorHandling(`${baseUrl}/hr_reports_api.php?action=upcoming_events`, 'events'),
      ]);

      // Set data states
      setDashboardData(dashboardDataResult);
      setEmployeeData(employeeDataResult);
      setAttendanceData(attendanceDataResult);
      setSalaryData(salaryDataResult);
      setLeaveData(leaveDataResult);
      setTurnoverData(turnoverDataResult);
      setDeptData(deptDataResult);
      setPayrollData(payrollDataResult);
      setPerformanceData(performanceDataResult);
      setDemographicsData(demographicsDataResult);
      setUpcomingEvents(eventsDataResult);
      
      setApiErrors(errors);

      const errorCount = Object.keys(errors).length;
      if (errorCount > 0) {
        setSnackbar({ 
          open: true, 
          message: `Loaded with ${errorCount} warning(s). Some data may be unavailable.`, 
          severity: 'warning' 
        });
      }

      console.log(`[HR Reports] All data loaded in ${(performance.now() - startTime).toFixed(2)}ms`);
    } catch (err) {
      console.error('[HR Reports] Error fetching data:', err);
      setSnackbar({ open: true, message: `Failed to load data: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedQuarter, debugLogData]);

  useEffect(() => {
    // Get user role
    try {
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const profile = JSON.parse(userProfile);
        setUserRole(profile.user_type || 'gm');
      }
    } catch (e) {
      console.error('Error parsing user profile:', e);
    }
    
    fetchAllData();
  }, [fetchAllData]);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // ============================================================================
  // EXPORT FUNCTIONALITY - COMPLETE STAFF DATA EXPORT
  // ============================================================================
 // src/components/HR/HrReportsDashboard.js - Updated export function

const exportStaffData = useCallback(async () => {
  setExporting(true);
  const startTime = performance.now();
  
  console.log('[HR Export] =========================================');
  console.log('[HR Export] Starting staff data export');
  console.log('[HR Export] User role:', userRole);
  console.log('[HR Export] Selected year:', selectedYear);
  console.log('[HR Export] Selected month:', selectedMonth);
  
  try {
    const baseUrl = getApiBaseUrl();
    // Use the full URL with proper protocol
    const exportUrl = `${baseUrl}/export_staff_data.php`;
    
    console.log('[HR Export] Export URL:', exportUrl);
    
    // Get auth token
    const authToken = localStorage.getItem('authToken');
    const userProfile = localStorage.getItem('userProfile');
    
    console.log('[HR Export] Authentication present:', !!authToken);
    console.log('[HR Export] User profile present:', !!userProfile);
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.warn('[HR Export] Request timeout - aborting');
      controller.abort();
    }, 120000); // 120 second timeout for large files
    
    // Make the request with mode: 'cors' and credentials: 'include'
    const response = await fetch(exportUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Accept': 'application/vnd.ms-excel, application/octet-stream',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Export-Timestamp': Date.now().toString(),
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    console.log('[HR Export] Response status:', response.status);
    console.log('[HR Export] Response OK:', response.ok);
    console.log('[HR Export] Response headers:', {
      contentType: response.headers.get('Content-Type'),
      contentDisposition: response.headers.get('Content-Disposition'),
      contentLength: response.headers.get('Content-Length'),
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorText = await response.text();
        if (errorText && errorText.length < 500) {
          errorMessage = errorText;
        }
      } catch (e) {
        console.error('[HR Export] Could not read error response');
      }
      throw new Error(errorMessage);
    }
    
    // Get the blob from response
    const blob = await response.blob();
    const duration = (performance.now() - startTime).toFixed(2);
    
    console.log('[HR Export] Blob received:', {
      size: `${(blob.size / 1024).toFixed(2)} KB`,
      type: blob.type,
      duration: `${duration}ms`
    });
    
    if (blob.size === 0) {
      throw new Error('Empty response received from server');
    }
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from Content-Disposition or generate
    let filename = `staff_nominal_roll_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xls`;
    
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    
    setSnackbar({ 
      open: true, 
      message: `Staff data exported successfully! (${(blob.size / 1024).toFixed(2)} KB)`, 
      severity: 'success' 
    });
    
    console.log('[HR Export] Export completed successfully in', duration, 'ms');
    
  } catch (error) {
    const duration = (performance.now() - startTime).toFixed(2);
    console.error('[HR Export] Export failed after', duration, 'ms:', error);
    
    let userMessage = 'Export failed: ';
    if (error.name === 'AbortError') {
      userMessage += 'Request timeout. Please try again.';
    } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      userMessage += 'Network error. Please check:';
      userMessage += '\n• Your internet connection';
      userMessage += '\n• The server is running';
      userMessage += '\n• CORS headers are configured';
    } else {
      userMessage += error.message;
    }
    
    setSnackbar({ 
      open: true, 
      message: userMessage, 
      severity: 'error' 
    });
  } finally {
    setExporting(false);
  }
}, [userRole, selectedYear, selectedMonth]);


  // Stat Card Component
  const StatCard = ({ title, value, subtitle, color, icon: Icon, trend, trendValue, loading: cardLoading }) => (
    <Card sx={{ 
      borderRadius: 3, 
      height: '100%',
      background: `linear-gradient(135deg, ${color}08 0%, ${color}02 100%)`,
      borderLeft: `4px solid ${color}`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, mb: 1 }}>
              {title}
            </Typography>
            {cardLoading ? (
              <Skeleton variant="text" width={100} height={50} />
            ) : (
              <Typography variant="h4" fontWeight="bold" sx={{ color: color }}>
                {value}
              </Typography>
            )}
            {subtitle && !cardLoading && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
            {trend && !cardLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {trend === 'up' ? (
                  <TrendingUpIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, color: '#f44336' }} />
                )}
                <Typography variant="caption" sx={{ color: trend === 'up' ? '#4caf50' : '#f44336', ml: 0.5 }}>
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  // Skeleton loader
  const TableSkeleton = ({ rows = 5, cols = 7 }) => (
    <Box sx={{ p: 2 }}>
      {[...Array(rows)].map((_, idx) => (
        <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {[...Array(cols)].map((_, colIdx) => (
            <Skeleton key={colIdx} variant="text" width={`${100 / cols}%`} height={40} />
          ))}
        </Box>
      ))}
    </Box>
  );

  // Render Dashboard Tab
  const renderDashboardTab = () => {
    if (loading && !dashboardData) return <TableSkeleton rows={3} cols={4} />;
    if (!dashboardData) return <Alert severity="warning">No dashboard data available</Alert>;
    
    const summary = dashboardData.summary || {};
    
    return (
      <Box>
        {/* Key Metrics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Employees"
              value={formatNumber(dashboardData.total_employees)}
              subtitle="Active Staff"
              color="#1976d2"
              icon={PeopleIcon}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="New Hires"
              value={formatNumber(dashboardData.new_hires)}
              subtitle={`Year ${selectedYear}`}
              color="#4caf50"
              icon={TrendingUpIcon}
              trend={dashboardData.new_hires > 0 ? "up" : null}
              trendValue={`+${dashboardData.new_hires} this year`}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Terminations"
              value={formatNumber(dashboardData.terminations)}
              subtitle={`${dashboardData.turnover_rate}% turnover rate`}
              color="#f44336"
              icon={TrendingDownIcon}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Monthly Payroll"
              value={formatCurrency(summary.total_monthly_salary)}
              subtitle="Total base salary"
              color="#ff9800"
              icon={MoneyIcon}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* Second Row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Leave"
              value={formatNumber(summary.pending_leave)}
              subtitle="Awaiting approval"
              color="#ff9800"
              icon={EventNoteIcon}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="On Leave"
              value={formatNumber(summary.employees_on_leave)}
              subtitle={`Currently on leave`}
              color="#2196f3"
              icon={EventNoteIcon}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Reviews"
              value={formatNumber(summary.pending_reviews)}
              subtitle="Staff appraisals"
              color="#9c27b0"
              icon={AwardIcon}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Birthdays"
              value={formatNumber(summary.birthdays_this_month)}
              subtitle={`${MONTHS[selectedMonth - 1]} ${selectedYear}`}
              color="#e91e63"
              icon={CakeIcon}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Department Distribution
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (dashboardData.department_distribution || []).length === 0 ? (
                <Alert severity="info">No department data available</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dashboardData.department_distribution} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="department" width={100} />
                    <RechartsTooltip formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="count" fill="#1976d2" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Gender Distribution
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (dashboardData.gender_distribution || []).length === 0 ? (
                <Alert severity="info">No gender data available</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboardData.gender_distribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ gender, percent }) => `${gender}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      dataKey="count"
                      nameKey="gender"
                    >
                      {(dashboardData.gender_distribution || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Monthly Hiring Trend (Last 12 Months)
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (dashboardData.monthly_hires || []).length === 0 ? (
                <Alert severity="info">No hiring data available</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={dashboardData.monthly_hires}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatNumber(value)} />
                    <Area type="monotone" dataKey="count" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Average Salary by Department
              </Typography>
              {loading ? (
                <TableSkeleton rows={3} cols={4} />
              ) : (summary.avg_salary_by_dept || []).length === 0 ? (
                <Alert severity="info">No salary data available</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell>Department</TableCell>
                        <TableCell align="right">Employees</TableCell>
                        <TableCell align="right">Average Salary</TableCell>
                        <TableCell align="right">Total Salary</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(summary.avg_salary_by_dept || []).map((dept, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{dept.department}</TableCell>
                          <TableCell align="right">{formatNumber(dept.employee_count)}</TableCell>
                          <TableCell align="right">{formatCurrency(dept.avg_salary)}</TableCell>
                          <TableCell align="right">{formatCurrency(dept.avg_salary * dept.employee_count)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render Employees Tab
  const renderEmployeesTab = () => {
    if (loading && !employeeData) return <TableSkeleton rows={5} cols={7} />;
    if (!employeeData) return <Alert severity="warning">No employee data available</Alert>;
    
    return (
      <Box>
        {/* Tenure Distribution */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Employee Tenure Distribution
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : (employeeData.tenure_distribution || []).length === 0 ? (
            <Alert severity="info">No tenure data available</Alert>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={employeeData.tenure_distribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ tenure_range, percent }) => `${tenure_range}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  dataKey="count"
                  nameKey="tenure_range"
                >
                  {(employeeData.tenure_distribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Paper>

        {/* Employee List */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Active Employees ({employeeData.active_employees?.length || 0})
            </Typography>
            <TextField size="small" placeholder="Search employees..." sx={{ width: 250 }} />
          </Box>
          {loading ? (
            <TableSkeleton rows={5} cols={7} />
          ) : (employeeData.active_employees || []).length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="info">No active employees found</Alert>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Salary</TableCell>
                    <TableCell>Hire Date</TableCell>
                    <TableCell>Tenure</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(employeeData.active_employees || []).slice(0, 50).map((emp, idx) => (
                    <TableRow key={emp.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{emp.full_name || 'N/A'}</TableCell>
                      <TableCell>{emp.department || 'N/A'}</TableCell>
                      <TableCell>{emp.role || 'Staff'}</TableCell>
                      <TableCell align="right">{formatCurrency(emp.current_salary)}</TableCell>
                      <TableCell>{formatDate(emp.hire_date)}</TableCell>
                      <TableCell>
                        {emp.tenure_months ? `${Math.floor(emp.tenure_months / 12)}y ${emp.tenure_months % 12}m` : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    );
  };

  // Render Salary Tab
  const renderSalaryTab = () => {
    if (loading && !salaryData) return <TableSkeleton rows={3} cols={4} />;
    if (!salaryData) return <Alert severity="warning">No salary data available</Alert>;
    
    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Salary Distribution
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (salaryData.salary_distribution || []).length === 0 ? (
                <Alert severity="info">No salary distribution data available</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={salaryData.salary_distribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="salary_range" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="count" fill="#4caf50" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Bonus by Month
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (salaryData.bonus_by_month || []).length === 0 ? (
                <Alert severity="info">No bonus data available</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={salaryData.bonus_by_month}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bonus_month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Line type="monotone" dataKey="total_bonus" stroke="#ff9800" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Top 10 Highest Paid Employees
              </Typography>
              {loading ? (
                <TableSkeleton rows={5} cols={4} />
              ) : (salaryData.highest_paid || []).length === 0 ? (
                <Alert severity="info">No employee salary data available</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="right">Salary</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(salaryData.highest_paid || []).map((emp, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{emp.full_name}</TableCell>
                          <TableCell>{emp.department}</TableCell>
                          <TableCell align="right">{formatCurrency(emp.current_salary)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Recent Salary Adjustments
              </Typography>
              {loading ? (
                <TableSkeleton rows={3} cols={8} />
              ) : (salaryData.salary_adjustments || []).length === 0 ? (
                <Alert severity="info">No salary adjustments found</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Previous</TableCell>
                        <TableCell align="right">New</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(salaryData.salary_adjustments || []).slice(0, 20).map((adj, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{adj.full_name}</TableCell>
                          <TableCell>{adj.department}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={adj.adjustment_type} 
                              color={adj.adjustment_type === 'increment' ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(adj.amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(adj.previous_salary)}</TableCell>
                          <TableCell align="right">{formatCurrency(adj.new_salary)}</TableCell>
                          <TableCell>{formatDate(adj.adjustment_date)}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={adj.approval_status}
                              color={adj.approval_status === 'approved' ? 'success' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render Leave Tab
  const renderLeaveTab = () => {
    if (loading && !leaveData) return <TableSkeleton rows={3} cols={4} />;
    if (!leaveData) return <Alert severity="warning">No leave data available</Alert>;
    
    const hasLeaveRequests = (leaveData.requests_by_status || []).length > 0;
    const hasPendingRequests = (leaveData.pending_requests || []).length > 0;
    
    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Leave Requests by Status
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : !hasLeaveRequests ? (
                <Alert severity="info">No leave requests found for {selectedYear}</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={leaveData.requests_by_status}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ status, percent }) => `${status}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      dataKey="count"
                      nameKey="status"
                    >
                      {(leaveData.requests_by_status || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Leave Balance Summary ({selectedYear})
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={150} />
              ) : leaveData.balance_summary ? (
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Avg Annual Leave</Typography>
                      <Typography variant="h4">{Math.round(leaveData.balance_summary.avg_annual_used || 0)} / {Math.round(leaveData.balance_summary.avg_annual_total || 20)}</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={((leaveData.balance_summary.avg_annual_used || 0) / (leaveData.balance_summary.avg_annual_total || 20)) * 100} 
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">Avg Sick Leave</Typography>
                      <Typography variant="h4">{Math.round(leaveData.balance_summary.avg_sick_used || 0)} / {Math.round(leaveData.balance_summary.avg_sick_total || 10)}</Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={((leaveData.balance_summary.avg_sick_used || 0) / (leaveData.balance_summary.avg_sick_total || 10)) * 100} 
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Alert severity="info">No leave balance data available</Alert>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Pending Leave Requests
              </Typography>
              {loading ? (
                <TableSkeleton rows={3} cols={7} />
              ) : !hasPendingRequests ? (
                <Alert severity="info">No pending leave requests</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Leave Type</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell align="center">Duration</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(leaveData.pending_requests || []).map((req, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{req.full_name}</TableCell>
                          <TableCell>{req.department}</TableCell>
                          <TableCell>
                            <Chip size="small" label={req.leave_type} />
                          </TableCell>
                          <TableCell>{formatDate(req.start_date)}</TableCell>
                          <TableCell>{formatDate(req.end_date)}</TableCell>
                          <TableCell align="center">{req.duration_days} days</TableCell>
                          <TableCell>{req.reason?.substring(0, 50)}...</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render Demographics Tab
  const renderDemographicsTab = () => {
    if (loading && !demographicsData) return <TableSkeleton rows={2} cols={2} />;
    if (!demographicsData) return <Alert severity="warning">No demographics data available</Alert>;
    
    const hasAgeData = (demographicsData.age_distribution || []).length > 0;
    const hasMaritalData = (demographicsData.marital_status || []).length > 0;
    
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Age Distribution
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : !hasAgeData ? (
                <Alert severity="info">No age distribution data available</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={demographicsData.age_distribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="age_range" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="count" fill="#2196f3" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Marital Status
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : !hasMaritalData ? (
                <Alert severity="info">No marital status data available</Alert>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={demographicsData.marital_status}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ marital_status, percent }) => `${marital_status}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      dataKey="count"
                      nameKey="marital_status"
                    >
                      {(demographicsData.marital_status || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Top 10 States of Origin
              </Typography>
              {loading ? (
                <TableSkeleton rows={5} cols={3} />
              ) : (demographicsData.top_states || []).length === 0 ? (
                <Alert severity="info">No state of origin data available</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell>State</TableCell>
                        <TableCell align="right">Number of Employees</TableCell>
                        <TableCell align="right">Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(demographicsData.top_states || []).map((state, idx) => {
                        const total = (demographicsData.top_states || []).reduce((sum, s) => sum + s.count, 0);
                        const percentage = total > 0 ? (state.count / total) * 100 : 0;
                        return (
                          <TableRow key={idx} hover>
                            <TableCell>{state.state_of_origin || 'Not Specified'}</TableCell>
                            <TableCell align="right">{state.count}</TableCell>
                            <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Render Upcoming Events Tab
  const renderEventsTab = () => {
    if (loading && !upcomingEvents) return <TableSkeleton rows={3} cols={1} />;
    if (!upcomingEvents) return <Alert severity="warning">No upcoming events data available</Alert>;
    
    const hasUpcomingBirthdays = (upcomingEvents.upcoming_birthdays || []).length > 0;
    const hasLeavesEnding = (upcomingEvents.leaves_ending_soon || []).length > 0;
    const hasAnniversaries = (upcomingEvents.work_anniversaries || []).length > 0;
    
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CakeIcon color="primary" /> Upcoming Birthdays
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : !hasUpcomingBirthdays ? (
                <Alert severity="info" icon={<CakeIcon />}>
                  No birthdays in the next 60 days
                </Alert>
              ) : (
                (upcomingEvents.upcoming_birthdays || []).map((birthday, idx) => (
                  <Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="body1" fontWeight="bold">{birthday.full_name}</Typography>
                    <Typography variant="body2" color="textSecondary">{birthday.department}</Typography>
                    <Chip size="small" label={birthday.birthday_date} sx={{ mt: 0.5 }} />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Turns {birthday.age_turning || birthday.age || '?'} years
                    </Typography>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventNoteIcon color="warning" /> Leaves Ending Soon
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : !hasLeavesEnding ? (
                <Alert severity="info" icon={<EventNoteIcon />}>
                  No leaves ending in the next 7 days
                </Alert>
              ) : (
                (upcomingEvents.leaves_ending_soon || []).map((leave, idx) => (
                  <Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 2 }}>
                    <Typography variant="body1" fontWeight="bold">{leave.full_name}</Typography>
                    <Typography variant="body2" color="textSecondary">{leave.department}</Typography>
                    <Typography variant="body2">Returns: {formatDate(leave.end_date)}</Typography>
                    <Chip size="small" label={`${leave.days_remaining} days left`} color="warning" sx={{ mt: 0.5 }} />
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CelebrationIcon color="success" /> Work Anniversaries
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : !hasAnniversaries ? (
                <Alert severity="info" icon={<CelebrationIcon />}>
                  No work anniversaries this month
                </Alert>
              ) : (
                (upcomingEvents.work_anniversaries || []).map((anniv, idx) => (
                  <Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                    <Typography variant="body1" fontWeight="bold">{anniv.full_name}</Typography>
                    <Typography variant="body2" color="textSecondary">{anniv.department}</Typography>
                    <Typography variant="body2">Hired: {formatDate(anniv.hire_date)}</Typography>
                    <Chip 
                      size="small" 
                      label={anniv.anniversary_type || `${anniv.years_of_service} years`} 
                      color="success" 
                      sx={{ mt: 0.5 }} 
                    />
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Tab configuration
  const tabs = [
    { label: 'Dashboard', icon: <DashboardIcon />, component: renderDashboardTab },
    { label: 'Employees', icon: <PeopleIcon />, component: renderEmployeesTab },
    { label: 'Salary', icon: <MoneyIcon />, component: renderSalaryTab },
    { label: 'Leave', icon: <EventNoteIcon />, component: renderLeaveTab },
    { label: 'Demographics', icon: <PeopleAltIcon />, component: renderDemographicsTab },
    { label: 'Events', icon: <CalendarIcon />, component: renderEventsTab },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{ 
        flexGrow: 1, 
        p: 3, 
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5, 
        maxWidth: '1600px', 
        mx: 'auto', 
        width: '100%', 
        overflow: 'auto', 
        height: '100vh',
        transition: 'margin-left 0.3s ease-in-out',
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
          }}
          onNotificationClick={() => console.log('Notifications clicked')}
          toggleSidebar={toggleSidebar}
        />
        
        {/* Header */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)', 
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                HR Reports Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Comprehensive HR analytics and workforce insights
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'white', borderRadius: 1 }}>
                <InputLabel>Year</InputLabel>
                <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year">
                  {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />} 
                onClick={fetchAllData}
                disabled={loading}
                sx={{ bgcolor: 'white', color: '#1565C0', '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                Refresh
              </Button>
              <Button 
                variant="contained" 
                startIcon={exporting ? <CircularProgress size={20} /> : <DescriptionIcon />} 
                onClick={exportStaffData}
                disabled={exporting}
                sx={{ bgcolor: 'white', color: '#1565C0', '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                {exporting ? 'Exporting...' : 'Export Staff Data'}
              </Button>
            </Box>
          </Box>
        </Paper>
        
        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': { py: 2, fontWeight: 'bold', textTransform: 'none', minHeight: 64 }
            }}
          >
            {tabs.map((tab, idx) => (
              <Tab key={idx} icon={tab.icon} iconPosition="start" label={tab.label} />
            ))}
          </Tabs>
        </Paper>
        
        {/* Tab Content */}
        <Fade in={!loading} timeout={300}>
          <Box>
            {tabs[activeTab].component()}
          </Box>
        </Fade>
        
        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            © {new Date().getFullYear()} Elegantè IT - HR Analytics Dashboard. All rights reserved.
          </Typography>
        </footer>
      </Box>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HrReportsDashboard;